import { Matrix4, Quaternion, Vector3 } from 'three/webgpu';
import type { Phase } from './Attack';
import type { Weapon } from './weapons';

/**
 * How the hands hold the weapon, in view space (metres from the eye: x right, y up, −z ahead).
 */
export interface HeldPose {
  /** Where the main hand grips. */
  grip: Vector3;
  /** The way the weapon points from the grip (unit). */
  dir: Vector3;
  /** The way its edge, or a hammer's striking face, faces (unit, across `dir`). */
  edge: Vector3;
}

/** What the motion reads from the attack each frame. */
export interface AttackState {
  phase: Phase;
  /** Seconds in the phase, and the charge (0–1). */
  time: number;
  charge: number;
  weapon: Weapon;
}

/** The aim's turning (rad/s) is smoothed over this long (s). */
const AIM_SMOOTH = 0.07;
/** Below this turn rate (rad/s) the aim counts as still: a strike from stillness is a stab. */
const STILL = 0.35;
/** At this turn rate (rad/s) the swing is fully a slash (or chop). */
const SWEEPING = 1.8;
/** How far (radians) a cut's windup draws the weapon back, against the aim's motion. */
const BACK = 1.2;
/** How far past the aim (radians) a cut carries. */
const FOLLOW = 0.55;
/**
 * The press point holds the windup at most this far off the aim (radians): further, and the
 * weapon follows the aim as it moves.
 */
const ANCHOR_REACH = 0.4;
/** How fast poses ease into each other outside the strike (1/s). */
const EASE = 15;

/**
 * The weapon held at rest: close to the body, below and a little right of the aim, pointing
 * just under it: ready to go any way. Per kind: a sword's blade up and forward, a hammer's head
 * up by the chest, a spear's point low ahead.
 */
function neutral(w: Weapon, out: HeldPose): HeldPose {
  if (w.kind === 'spear') {
    out.grip.set(0.15, -0.3, -0.14);
    aimPoint(0, -0.13, 3, out.dir).sub(out.grip).normalize();
  } else if (w.kind === 'hammer') {
    // The head just under the aim: a war hammer upright at the right, a maul across the body.
    const maul = w.id === 'maul';
    out.grip.set(maul ? 0.14 : 0.12, maul ? -0.44 : -0.36, maul ? -0.26 : -0.3);
    out.dir.set(maul ? -0.3 : 0.02, maul ? 0.5 : 0.55, -1).normalize();
  } else {
    const great = w.id === 'greatsword';
    out.grip.set(great ? 0.08 : 0.12, great ? -0.34 : -0.3, -0.32);
    aimPoint(0, -0.03, 1.6, out.dir).sub(out.grip).normalize();
  }
  out.edge.set(1, 0, 0);
  return out;
}

/**
 * The point `dist` m out along the view direction h radians right of the aim and v up from it
 * (past ±π/2 it goes on round behind the eye: a weapon drawn back over the shoulder).
 */
export function aimPoint(h: number, v: number, dist: number, out: Vector3): Vector3 {
  const c = Math.cos(v);
  return out.set(Math.sin(h) * c, Math.sin(v), -Math.cos(h) * c).multiplyScalar(dist);
}

const _d = new Vector3();
const _f = new Vector3();
const _m = new Matrix4();
const _x = new Vector3();
const _z = new Vector3();
const _inv = new Quaternion();

function makePose(): HeldPose {
  return { grip: new Vector3(), dir: new Vector3(0, 1, 0), edge: new Vector3(1, 0, 0) };
}

function copyPose(to: HeldPose, from: HeldPose): HeldPose {
  to.grip.copy(from.grip);
  to.dir.copy(from.dir);
  to.edge.copy(from.edge);
  return to;
}

/** `to` eased toward `target` by k (0–1): grip linearly, directions along the sphere. */
function easePose(to: HeldPose, target: HeldPose, k: number): void {
  to.grip.lerp(target.grip, k);
  to.dir.lerp(target.dir, k).normalize();
  to.edge.lerp(target.edge, k);
  // Keep the edge across the weapon.
  to.edge.addScaledVector(to.dir, -to.edge.dot(to.dir));
  if (to.edge.lengthSq() < 1e-6) to.edge.set(1, 0, 0).addScaledVector(to.dir, -to.dir.x);
  to.edge.normalize();
}

/** Smooth ease-out (fast start, gentle end). */
function easeOut(t: number): number {
  const u = Math.min(1, Math.max(0, t));
  return 1 - (1 - u) ** 2.4;
}

/**
 * The weapon in the hands, moved by the attack and by how the player moves the aim:
 * - at rest it's held close and below the aim, ready to go any way (`neutral`);
 * - pressing the button swings it toward where the press began (the aim then, held in the
 *   world as the view turns), cocked back against the way the aim is moving;
 * - on the release it strikes out and through the aim, the way the aim was moving: a slash, a
 *   chop or an uppercut, or a thrust toward the press point if the aim was still;
 * - then it comes back to rest.
 * The hits themselves follow the aim (reach.ts): the weapon goes where they land.
 */
export class WeaponMotion {
  /** This frame's pose (view space). */
  readonly pose = makePose();
  /** The aim's turning (rad/s across and up the view), smoothed. */
  readonly turn = { h: 0, v: 0 };
  private readonly target = makePose();
  private readonly from = makePose();
  private readonly end = makePose();
  private readonly rest = makePose();
  /** Where the aim was when the button went down (world direction). */
  private readonly anchor = new Vector3(0, 0, -1);
  private readonly lastForward = new Vector3();
  private hasLast = false;
  private phase: Phase = 'idle';
  private weaponId = '';
  /** The strike's way across the view (unit) and how much it swept (0 stab … 1 slash), frozen at the release. */
  private sweepH = 0;
  private sweepV = 0;
  private sweep = 0;
  private clock = 0;
  /** The press point's angles across and up from the aim (radians, clamped to ANCHOR_REACH). */
  private ah = 0;
  private av = 0;

  /** The current weapon's way of being held at rest. */
  restPose(w: Weapon): HeldPose {
    return neutral(w, this.rest);
  }

  /** Per frame: the pose for the attack's state, with the view turned to `viewQ` (world). */
  update(dt: number, a: AttackState, viewQ: Quaternion): HeldPose {
    this.clock += dt;
    const w = a.weapon;
    const forward = _f.set(0, 0, -1).applyQuaternion(viewQ);
    _inv.copy(viewQ).invert();
    // How the aim turned since the last frame, across the view (rad/s).
    if (this.hasLast && dt > 0) {
      _d.copy(this.lastForward).applyQuaternion(_inv);
      const h = -Math.atan2(_d.x, -_d.z) / dt;
      const v = -Math.atan2(_d.y, -_d.z) / dt;
      const k = 1 - Math.exp(-dt / AIM_SMOOTH);
      this.turn.h += (h - this.turn.h) * k;
      this.turn.v += (v - this.turn.v) * k;
    }
    this.lastForward.copy(forward);
    this.hasLast = true;
    if (w.id !== this.weaponId) {
      this.weaponId = w.id;
      copyPose(this.pose, neutral(w, this.rest));
    }
    if (a.phase !== this.phase) {
      copyPose(this.from, this.pose);
      if (a.phase === 'windup') this.anchor.copy(forward);
      if (a.phase === 'strike') this.planStrike(w, viewQ);
      this.phase = a.phase;
    }
    neutral(w, this.rest);
    if (a.phase === 'strike') {
      const t = easeOut(a.time / Math.max(w.strikeDelay, 0.05));
      copyPose(this.pose, this.from);
      easePose(this.pose, this.end, t);
      // Out from the body through the middle of the swing.
      this.pose.grip.z -= 0.1 * Math.sin(Math.PI * t) * this.sweep;
      return this.pose;
    }
    if (a.phase === 'recover') {
      const t = easeOut(a.time / Math.max(w.recovery, 0.05));
      copyPose(this.pose, this.from);
      easePose(this.pose, this.rest, t);
      return this.pose;
    }
    if (a.phase === 'windup') this.windup(w, a.charge, viewQ);
    else {
      // At rest: breathing, and trailing a little behind the aim as it turns.
      copyPose(this.target, this.rest);
      this.target.grip.x -= clamp(this.turn.h * 0.012, -0.04, 0.04);
      this.target.grip.y += Math.sin(this.clock * 1.7) * 0.004 - clamp(this.turn.v * 0.012, -0.04, 0.04);
    }
    easePose(this.pose, this.target, 1 - Math.exp(-dt * EASE));
    return this.pose;
  }

  /** Where the press point is from today's aim (it stays put in the world as the view turns). */
  private anchorAngles(viewQ: Quaternion): void {
    _d.copy(this.anchor).applyQuaternion(_inv.copy(viewQ).invert());
    this.ah = clamp(Math.atan2(_d.x, -_d.z), -ANCHOR_REACH, ANCHOR_REACH);
    this.av = clamp(Math.atan2(_d.y, Math.hypot(_d.x, _d.z)), -ANCHOR_REACH, ANCHOR_REACH);
  }

  /**
   * The windup: toward where the press began, then following the aim as it moves, drawn back
   * against the motion for a cut. Held still, a sword or spear points at the press point for a
   * thrust and a hammer goes up overhead for a blow straight down onto it.
   */
  private windup(w: Weapon, charge: number, viewQ: Quaternion): void {
    this.anchorAngles(viewQ);
    const { ah, av } = this;
    const speed = Math.hypot(this.turn.h, this.turn.v);
    const s = smooth(STILL, SWEEPING, speed);
    const mh = speed > 1e-6 ? this.turn.h / speed : 0;
    const mv = speed > 1e-6 ? this.turn.v / speed : 0;
    const t = this.target;
    if (w.kind === 'spear') {
      // Pulled back along the line to the press point, a little against the motion.
      const wh = ah - mh * s * 0.3;
      const wv = av - mv * s * 0.3;
      t.grip.set(0.16 + 0.12 * wh, -0.27 + 0.12 * wv, -0.05 + 0.1 * charge);
      aimPoint(wh, wv - 0.05, 3, t.dir)
        .sub(t.grip)
        .normalize();
      edgeToward(t, mh * s, mv * s);
      return;
    }
    // A cut: drawn back past the press point, the way the aim came from (lifted for a level cut).
    const back = BACK * (0.75 + 0.25 * charge);
    const cutH = ah - mh * back;
    const cutV = av - mv * back + 0.15 * Math.abs(mh);
    // Still: a sword's thrust aims at the press point; a hammer is raised overhead.
    const hammer = w.kind === 'hammer';
    const stillH = hammer ? ah * 0.5 : ah;
    const stillV = hammer ? 1.25 + 0.15 * charge : av;
    const h = clamp(stillH + (cutH - stillH) * s, -1.3, 1.3);
    const v = clamp(stillV + (cutV - stillV) * s, -1, 1.8);
    // The hands go to the side the weapon is drawn to (kept at the edge of the view), up and in
    // for a raised one, off to the right of the aim.
    t.grip.set(
      0.14 + 0.18 * Math.sin(h),
      -0.25 + 0.27 * Math.sin(Math.min(v, 1.2)),
      -0.32 + 0.08 * Math.max(0, Math.sin(v)),
    );
    aimPoint(h, v, 1.6, t.dir).sub(t.grip).normalize();
    // The edge (a hammer's face) toward the way the swing will go: down, for the overhead blow.
    edgeToward(t, mh * s, mv * s - (hammer ? 1 - s : 0));
  }

  /** At the release: where the strike ends, from the aim's motion and the press point. */
  private planStrike(w: Weapon, viewQ: Quaternion): void {
    const speed = Math.hypot(this.turn.h, this.turn.v);
    this.sweep = smooth(STILL, SWEEPING, speed);
    this.sweepH = speed > 1e-6 ? this.turn.h / speed : 0;
    this.sweepV = speed > 1e-6 ? this.turn.v / speed : 0;
    this.anchorAngles(viewQ);
    const s = this.sweep;
    const hammer = w.kind === 'hammer';
    // A cut ends past the aim, the way it was moving; a thrust goes out toward the press point,
    // and a hammer's overhead blow comes down onto it.
    const eh = this.sweepH * s * FOLLOW + this.ah * 0.5 * (1 - s);
    const ev = this.sweepV * s * FOLLOW + (this.av * 0.5 - (hammer ? 0.4 : 0)) * (1 - s);
    const e = this.end;
    const reach = w.kind === 'spear' ? 0.55 : hammer ? 0.18 : 0.25;
    e.grip.set(
      0.05 + 0.3 * Math.sin(eh),
      -0.24 + 0.24 * Math.sin(ev),
      -0.4 - reach * (1 - 0.4 * s) - 0.12 * s,
    );
    aimPoint(eh, ev, 2.4, e.dir).sub(e.grip).normalize();
    edgeToward(e, this.sweepH * s, this.sweepV * s - (hammer ? 1 - s : 0));
  }
}

/** Turn the pose's edge toward (h, v) across the view (or level, for a thrust), kept across its direction. */
function edgeToward(p: HeldPose, h: number, v: number): void {
  if (Math.hypot(h, v) < 0.15) p.edge.set(1, 0, 0);
  else p.edge.set(h, v, 0);
  p.edge.addScaledVector(p.dir, -p.edge.dot(p.dir));
  if (p.edge.lengthSq() < 1e-6) p.edge.set(1, 0, 0).addScaledVector(p.dir, -p.dir.x);
  p.edge.normalize();
}

/** The pose's turn (model +Y along `dir`, +X along `edge`). */
export function poseQuaternion(p: HeldPose, out: Quaternion): Quaternion {
  _z.crossVectors(p.edge, p.dir).normalize();
  _x.crossVectors(p.dir, _z).normalize();
  _m.makeBasis(_x, p.dir, _z);
  return out.setFromRotationMatrix(_m);
}

/** The off hand's place: `along` m up the weapon from the grip. */
export function handAlong(p: HeldPose, along: number, out: Vector3): Vector3 {
  return out.copy(p.grip).addScaledVector(p.dir, along);
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

function smooth(lo: number, hi: number, x: number): number {
  const t = clamp((x - lo) / (hi - lo), 0, 1);
  return t * t * (3 - 2 * t);
}
